import React, { useEffect, useMemo, useState } from 'react';
import axios, { PUBLIC_BASE } from '../utils/axiosInstance';
import ListCard from '../components/ListCard';

/** Normalize an image-ish value into a usable URL for <img> */
const toImageUrl = (raw) => {
  if (!raw) return '';
  // absolute already?
  if (typeof raw === 'string' && /^https?:\/\//i.test(raw)) return raw;
  return `${PUBLIC_BASE}${raw}`;
};

/** Try a couple endpoints so we work across slightly different backends */
async function fetchLists() {
  const tries = ['/rankings', '/lists'];
  let lastErr = null;
  for (const url of tries) {
    try {
      const res = await axios.get(url);
      const data = Array.isArray(res.data)
        ? res.data
        : (res.data?.results || res.data?.lists || []);
      if (Array.isArray(data)) return data;
    } catch (e) { lastErr = e; }
  }
  if (lastErr) throw lastErr;
  return [];
}

const RankMe = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [query, setQuery] = useState('');  // search query

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setErr('');
        setLoading(true);

        const rows = await fetchLists();

        // Map API rows → ListCard items
        const mapped = rows.map((l) => {
          const id =
            l._id ?? l.id ?? l.listId ?? (l.slug ? String(l.slug) : undefined);
          const title = l.listName ?? l.name ?? 'Untitled';

          // look for any plausible image field
          const rawImg =
            l.image ?? l.cover ?? l.imageUrl ?? l.imageURL ?? l.thumbnail ?? '';
          const imageUrl = toImageUrl(rawImg);

          const subtitle =
            l.category ??
            l.topic ??
            l.createdBy ??
            (l.owner ? `@${l.owner}` : 'unknown');

          return { id, title, imageUrl, subtitle };
        }).filter((x) => x.id);

        // De-dupe by id in case multiple endpoints overlap
        const deduped = [];
        const seen = new Set();
        for (const row of mapped) {
          if (seen.has(row.id)) continue;
          seen.add(row.id);
          deduped.push(row);
        }

        if (alive) setItems(deduped);
      } catch (e) {
        console.error('Failed to fetch lists', e);
        if (alive) setErr('Failed to load lists.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, []);

  // Live filter by title (case-insensitive)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (it.title || '').toLowerCase().includes(q));
  }, [items, query]);

  const onClear = () => setQuery('');

  return (
    <div className="scroll-shell">
      <div className="scroll-body rankme-page">
        {err && <div style={{ color:'#b91c1c', marginBottom:12 }}>{err}</div>}

        {/* Search bar */}
        <div className="rankme-search-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            maxWidth: 520,
            margin: '0px auto 10px',
          }}
        >
          <input
            className="input input-outline-black"
            type="text"
            value={query}
            placeholder="Search lists by name…"
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search lists"
            style={{ height: 40, flex: 1 }}
          />
          <button
            className="btn-outline-black"
            onClick={onClear}
            disabled={!query}
            style={{ height: 40, padding: '0 14px' }}
          >
            Clear
          </button>
        </div>

        {/* Divider aligned to grid width */}
        <div className="grid-divider" aria-hidden="true" />

        <div className="grid">
          {filtered.map((it) => (
            <ListCard
              key={it.id}
              id={it.id}
              title={it.title}
              listName={it.title}
              name={it.title}
              subtitle={it.subtitle}
              imageUrl={it.imageUrl}
              to={`/lists/${it.id}`}
            />
          ))}
        </div>

        {!loading && filtered.length === 0 && !err && (
          <div style={{ textAlign:'center', color:'#777', marginTop:24 }}>
            {query ? <>No lists match “{query}”.</> : <>No lists yet — once lists are available, they’ll show up here.</>}
          </div>
        )}
      </div>
    </div>
  );
};

export default RankMe;
